import type { Bookmark } from '@stashmark/shared';
import { extractDomain } from '@stashmark/shared';
import { Card, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit2, Trash2 } from 'lucide-react';

type Props = {
  bookmark: Bookmark;
  onEdit: (bookmark: Bookmark) => void;
  onDelete: (bookmark: Bookmark) => void;
};

export const BookmarkCard = ({ bookmark, onEdit, onDelete }: Props) => {
  const domain = extractDomain(bookmark.url) ?? bookmark.url;

  // Generating a stable gradient based on the URL length for placeholders
  const hue = (bookmark.url.length * 15) % 360;
  const placeholderStyle = {
    background: `linear-gradient(135deg, hsl(${hue}, 40%, 30%), hsl(${(hue + 40) % 360}, 60%, 20%))`,
  };

  return (
    <Card className="group overflow-hidden rounded-2xl border-transparent bg-card shadow-sm transition-all hover:shadow-md hover:ring-1 hover:ring-primary/20 flex flex-col h-[420px]">
      <div className="relative h-48 w-full border-b flex-shrink-0" style={placeholderStyle}>
        <div className="absolute right-3 top-3 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
          <Button
            variant="secondary"
            size="icon"
            className="h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background"
            onClick={() => onEdit(bookmark)}
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            variant="destructive"
            size="icon"
            className="h-8 w-8 rounded-full bg-destructive/80 backdrop-blur-sm hover:bg-destructive"
            onClick={() => onDelete(bookmark)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="mb-3 flex items-center gap-2">
          <div className="flex h-5 w-5 items-center justify-center rounded bg-secondary text-[10px] font-bold text-secondary-foreground uppercase overflow-hidden">
            {domain.charAt(0)}
          </div>
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {domain}
          </span>
        </div>

        <CardTitle className="mb-2 line-clamp-2 text-lg font-bold leading-tight">
          {bookmark.title ?? domain}
        </CardTitle>

        <CardDescription className="line-clamp-3 text-sm text-muted-foreground flex-1">
          {bookmark.description || 'No description provided for this bookmark.'}
        </CardDescription>

        <div className="mt-4 flex flex-wrap gap-2">
          <span className="rounded-md bg-primary/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-primary">
            UI DESIGN
          </span>
          <span className="rounded-md bg-secondary px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            INSPIRATION
          </span>
        </div>
      </div>
    </Card>
  );
};
